import React, { useImperativeHandle, useRef } from 'react'
import { styled } from 'styled-components'
import { space, tok, tokPx } from '../../../utils'
import { focusWithinOutline } from '../../utils'
import { Icon } from '../Icon'
import {
  BaseField,
  BaseFieldInputProps,
  Input,
  StyledValidationIcon,
} from './BaseFieldWrapper'

export interface DateFieldProps extends BaseFieldInputProps {
  ref?: React.Ref<HTMLInputElement>
  className?: string
  minDate?: string
  maxDate?: string
}

const DateFieldIcons = styled.div`
  position: absolute;
  right: ${space('primitives_space_0')};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 3;
  background: ${tok('semantics_input_fields_background_color')};
  height: 100%;
  padding-right: ${space('primitives_space_16')};
`
const IconCalendar = styled.button`
  display: flex;
  align-items: center;
  ${Icon} {
    color: ${tok('semantics_content_color')};
  }
  border-radius: ${tokPx('semantics_controls_xs_corner_radius')};
  ${focusWithinOutline()}
`
const BaseFieldStyled = styled(BaseField)`
  width: ${tokPx('primitives_area_size_200')};
  &[data-empty='true'] > ${Input} {
    color: ${tok('semantics_input_fields_placeholder_color')};
  }
`
const DateInput = styled(Input)`
  cursor: default;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  &::-webkit-inner-spin-button,
  &::-webkit-calendar-picker-indicator {
    display: none;
    pointer-events: none;
  }
  ${IconCalendar} {
    cursor: default;
  }
  
`
const StyledValidationDateFieldIcon = styled(StyledValidationIcon)`
  padding-right: ${space('primitives_space_6')};
`

function DateField({ ref, ...props }: DateFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => {
    if (!inputRef.current) {
      throw new Error('DateField inputRef is unexpectedly null')
    }
    return inputRef.current
  })

  const handleIconClick = () => {
    const input = inputRef.current
    if (!input)
      return

    if (typeof input.showPicker === 'function') {
      input.focus()
      setTimeout(() => input.showPicker(), 0)
      return
    }

    input.focus()
  }

  return (
    <BaseFieldStyled {...props}>
      <DateInput
        ref={inputRef}
        name={props.name}
        disabled={props.disabled}
        value={props.value}
        onChange={props.onChange}
        onBlur={props.onBlur}
        type="date"
        min={props.minDate}
        max={props.maxDate}
        aria-label={props.label}
      />
      <DateFieldIcons>
        <StyledValidationDateFieldIcon status={props.status ?? 'default'} />
        <IconCalendar onClick={handleIconClick} type="button" aria-label={`Select ${props.label}`}>
          <Icon size={20} glyph="Calendar" />
        </IconCalendar>
      </DateFieldIcons>
    </BaseFieldStyled>
  )
}
DateField.displayName = 'DateField'
export default styled(DateField)`
  position: relative;
`
